<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Configure connector</h1>

                    <a :href="`/#/admin/integrations/pipelines/details/${tid}/${id}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        <span>Back</span>
                    </a>

                    <a @click="handleRefresh" class="btn btn-link btn-sm">
                        <i class="fa fa-refresh" aria-hidden="true"></i>
                        <span>Refresh</span>
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div class="row">
                <div v-for="c in connectors" class="col col-4">
                    <div @click="handleAttachConnectorCommandV1(c.ConnectorId)" 
                         class="card text-dark bg-light mb-3" role="button" 
                         :class="{ 'border-primary': connectorId == c.ConnectorId }">
                        <img v-if="c.Provider?.LogoUrl" class="card-img-top m-1" :src="c.Provider?.LogoUrl" style="max-height: 100px;">
                        <div class="card-body">
                            <h5 class="card-title">{{c.Name}}</h5>
                            <p class="card-text">{{c.Description}}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
    import { guid } from "@app/Guid"
    import { useId, useOrganizationUnitId } from '@common/composables'
    import { useAdminConfigurePipelineConnectorPage } from '@Integrations/Pipelines/pages-core'

    document.title = "Configure connector"

    const tid = useOrganizationUnitId()
    const id = useId()

    const {
        useCurrentUserQuery,
        useConnectorsOfOrganizationQuery,
        usePipelineDetailsQuery,
        useAttachConnectorCommand
    } = useAdminConfigurePipelineConnectorPage()

    const connectorProviderElement = ref<HTMLIFrameElement>(null)

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { runPipelineDetailsQueryV1, modelPipelineDetailsQueryV1 } = usePipelineDetailsQuery(id)
    const direction = computed(() => modelPipelineDetailsQueryV1.value?.Direction)
    const name = computed(() => `Connector for pipeline ${modelPipelineDetailsQueryV1.value?.Name}`)
    const configPageUrl = computed(() => `${provider.value?.ConfigPageUrl}${tid}`)
    const connectorProviderType = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorProviderType)
    const connectorConfig = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorConfig)
    const connectorId = computed(() => modelPipelineDetailsQueryV1.value?.ConnectorId)

    const { runConnectorsOfOrganizationQueryV1, modelConnectorsOfOrganizationQueryV1 } = useConnectorsOfOrganizationQuery(tid)
    const connectors = computed(() => modelConnectorsOfOrganizationQueryV1.value?.Connectors || [])

    const { invokeAttachConnectorCommandV1, succeedAttachConnectorCommandV1 } = useAttachConnectorCommand(id, tid, userId)

    watch(connectorProviderType, (val) => {
        providerType.value = connectorProviderType.value
    })

    watch(succeedAttachConnectorCommandV1, () => {
        if (succeedAttachConnectorCommandV1.value) location.href = `/#/admin/integrations/pipelines/details/${tid}/${id}`
    })

    onMounted(() => {
        runCurrentUserQueryV1()
        runConnectorsOfOrganizationQueryV1()
        runPipelineDetailsQueryV1()
        window.addEventListener('message', onMessage);
    })

    onUnmounted(() => {
        window.removeEventListener('message', onMessage);
    })

    function onMessage(event) {
        if ((event.source as any)?.frameElement?.name == 'connectorProviderElement') {
            if (event.data?.type == 'Integrations.Connectors.ReadConfig') {
                // set current config once connector iframs is ready
                connectorProviderElement.value?.contentWindow?.postMessage({
                    'type': 'Integrations.Connectors.InitConfig',
                    'data': connectorConfig.value
                })
            }
            else if (event.data?.type == 'Integrations.Connectors.ApplyConnectorConfig') {
                config.value = JSON.stringify(event.data.data);
                invokeCreateConnectorCommandV1({ Id: connectorId.value })
            }
        }
    }

    function handleAttachConnectorCommandV1(connectorId: string) {
        invokeAttachConnectorCommandV1({ ConnectorId: connectorId })
    }

    function handleRefresh() {
        runConnectorsOfOrganizationQueryV1({ Rebuild: true })
    }
</script>